import { autoinject, observable, transient } from "aurelia-framework";
import { validateTrigger, ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../client-validation-system/custom-validation-renderer";
import { SalesforceCompanyTypes } from "../../../../constants/salesforce/salesforce-company-types";
import { SalesforceCountries } from "../../../../constants/salesforce/salesforce-countries";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../models/basics/basics.models";
import { SaveGhgVerificationResponse } from "../../../../models/ghgs/ghg.models";
import { AnonVesselDetailModel } from "../../../../models/vessels/vessel.models";
import { EmailRecipientTypeEnum } from "../../../../models/vetting/vetting.models";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { GhgService } from "../../../../services/ghg/ghg.service";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { EmailValidator } from "../../../../utilities/email-validator";
import { EntityFinderSearch } from "../../../components/entity-finder/entity-finder-search";
import { ExternalGhgVerificationModel } from "../../../layouts/layout-ghg-verification/layout-ghg-verification";
import { RequestAnEnvironmentalVerification } from "../../modules/ghg/pages/request-an-environmental-verification/request-an-environmental-verification";

@autoinject()
@transient()
export class RequestEnvironmentalReview {

    public readonly companyRoles: { name: string, isDocHolder: boolean }[] = [{
        name: "DOC Holder",
        isDocHolder: true
    }, {
        name: "Non-DOC Holder",
        isDocHolder: false
    }];
    public companyEntityTypeConfigurationModel: EntityTypeConfigurationModel[];
    public selectedCompany: any;
    public firstName: string;
    public lastName: string;
    public jobTitle: string;
    public yourCompanyEmailAddress: string;
    public step: "company-type-selection" | "company-info-form" | "env-verification-form" | "form-submitted" = "company-type-selection";
    public currentStep: "company-type-selection" | "company-info-form" | "env-verification-form" | "form-submitted" = "company-type-selection";
    public emailCorrespondences: string[] = [""];
    public verifyOptions: { id: number, name: string, isSelected: boolean, show: boolean }[];
    public marketing: boolean;
    public terms: boolean;
    public requestSubmitted: boolean;
    public externalGhgVerification: ExternalGhgVerificationModel;
    public existingGhg: number;
    public ghgSubmitted: boolean;
    public ghgAlreadyExists: boolean;
    public originalGoBackInEnvSubmission: () => void;
    public vessel: AnonVesselDetailModel;
    public companyEntityFinderSearch: EntityFinderSearch;
    public canCloseWindow: boolean;
    public selectedCountry: { isoCode: string, country: string };
    public selectedCompanyType: { id: number, name: string };
    public readonly companyTypes: { id: number, name: string }[] = SalesforceCompanyTypes;
    public readonly countries: { isoCode: string, country: string }[] = SalesforceCountries;

    @observable()
    public vesselImo: string;

    @observable()
    public selectedCompanyRole: { name: string, isDocHolder: boolean } = null;

    constructor(public validationsClientService: ValidationsClientService,
        public requestAnEnvironmentalVerification: RequestAnEnvironmentalVerification,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private notificationClientService: NotificationClientService,
        private manageVesselService: ManageVesselService,
        private ghgService: GhgService,
        private validationController: ValidationController,) {
        this.validationController.addRenderer(new CustomValidationRenderer());
        this.validationController.changeTrigger(validateTrigger.manual);
        this.companyEntityTypeConfigurationModel = this.entityTypeConfigurationHelper.getConfigurationByKey(["BasicCompanySearch"]);
        this.originalGoBackInEnvSubmission = this.requestAnEnvironmentalVerification.navigateBack;
        this.verifyOptions= this.requestAnEnvironmentalVerification.verifyOptions.filter((option) => option.show);
        //Check if its possible to close the tab, in order to show/hide the Close button at the end of the flow as window.close() does
        //not work when accessing the url directly
        this.canCloseWindow = window.opener != null || window.history.length == 1;
    }

    attached() {
        this.setValidations();
    }

    setValidations() {
        ValidationRules
            .ensure<RequestEnvironmentalReview, any>(x => x.selectedCompany)
            .required()
            .when(x => x.selectedCompanyRole.isDocHolder)
            .withMessage("DOC Company name is required")
            .ensure(x => x.firstName)
            .required()
            .withMessage("First name is required")
            .ensure(x => x.lastName)
            .required()
            .withMessage("Last name is required")
            .ensure(x => x.jobTitle)
            .required()
            .withMessage("Job title is required")
            .ensure(x => x.vesselImo)
            .required()
            .satisfies(() => this.vessel != null)
            .withMessage("Vessel IMO is ${vesselImo == null ? 'required':'not valid'}")
            .ensure(x => x.selectedCompanyType)
            .required()
            .withMessage("Company type is required")
            .ensure(x => x.yourCompanyEmailAddress)
            .satisfies(x => x != null && EmailValidator.isValidEmail(x))
            .when(x => !x.selectedCompanyRole.isDocHolder)
            .withMessage("Your company email address for correspondence is required")
            .ensure(x => x.selectedCountry)
            .required()
            .withMessage("Country is required")
            .ensure(x => x.emailCorrespondences)
            .required()
            .satisfies((x: string[]) => x && x.length > 0 && x.every(email => EmailValidator.isValidEmail(email)))
            .when(x => x.selectedCompanyRole.isDocHolder)
            .withMessage("Your email address for correspondence is required")
            .ensure(x => x.emailCorrespondences)
            .required()
            .satisfies((x: string[]) => x && x.length > 0 && x.every(email => EmailValidator.isValidEmail(email)))
            .when(x => !x.selectedCompanyRole.isDocHolder)
            .withMessage("DOC holder email address is required")
            .ensure(x => x.verifyOptions)
            .satisfies((_, o) => o.verifyOptions.some(x => x.isSelected))
            .withMessage("Select at least 1 verify option")
            .ensure(x => x.terms)
            .satisfies(x => x == true)
            .when(x => x.selectedCompanyRole.isDocHolder)
            .withMessage("Agreeing to terms is required")
            .on(this);

        this.validationController.addObject(this);
    }

    addEmail() {
        this.emailCorrespondences.push(null);
    }

    removeEmail(index: number) {
        this.emailCorrespondences.splice(index, 1);
    }

    onVerificationOptionToggled(verifyOption) {
        const evdiOption = this.verifyOptions[0];
        const eexiOption = this.verifyOptions[1];

        if (evdiOption.isSelected && eexiOption.isSelected) {
            if (verifyOption.id === evdiOption.id) eexiOption.isSelected = false;
            else evdiOption.isSelected = false;
        }
    }

    searchCompanySelected(e: any) {
        if (e) {
            this.selectedCompany = e;
        } else {
            this.selectedCompany = null;
        }
    }

    openNewTab(url: string, $event) {
        $event.preventDefault();
        $event.stopPropagation();
        const win = window.open(url, '_blank');
        win.focus();
    }

    goBack = () => {
        if (this.currentStep == "company-info-form") {
            this.currentStep = "company-type-selection";
            this.selectedCompanyRole = null;
        } else if (this.currentStep == "env-verification-form") {
            if (this.requestAnEnvironmentalVerification.currentTabIndex == 1) {
                this.currentStep = "company-info-form";
            } else {
                this.originalGoBackInEnvSubmission();
            }
        }
    }

    closePage() {
        window.close();
    }

    vesselImoChanged() {
        if (this.vesselImo) {
            this.manageVesselService.getVesselAnonymously({
                vesselId: null,
                imo: this.vesselImo,
                bKey: null
            }).then(response => {
                this.vessel = response.vessel;
            });
        } else {
            this.vessel = null;
        }
    }

    selectedCompanyRoleChanged(newValue, oldValue) {
        if (newValue && oldValue) {
            setTimeout(() => {
                this.resetFields();
            });
        }
    }

    resetFields() {
        this.firstName = null;
        this.lastName = null;
        this.vesselImo = null;
        this.yourCompanyEmailAddress = null;
        this.emailCorrespondences = [""];
        this.vessel = null;
        this.marketing = null;
        this.terms = null;
        this.selectedCompany = null;
        if (this.companyEntityFinderSearch) {
            this.companyEntityFinderSearch.removeFilter();
        }
        this.verifyOptions.forEach(x => {
            x.isSelected = false;
        });
    }

    async next() {

        if (this.currentStep == "company-type-selection") {
            this.currentStep = "company-info-form";
            setTimeout(() => {
                if (!this.selectedCompany && this.companyEntityFinderSearch) {
                    this.companyEntityFinderSearch.removeFilter();
                }
            });
        } else if (this.currentStep == "company-info-form") {
            try {
                const validationResult = await this.validationController.validate();
                if (validationResult.valid) {
                    if (!this.selectedCompanyRole.isDocHolder) {
                        const response = await this.ghgService.sendInviteToDoc({
                            companyEmailAddress: this.yourCompanyEmailAddress,
                            emailCorrespondences: this.emailCorrespondences,
                            firstName: this.firstName,
                            lastName: this.lastName,
                            jobTitle: this.jobTitle,
                            verifyOptions: this.verifyOptions.filter(x => x.isSelected).map(x => x.name),
                            vesselIImo: this.vesselImo,
                            vesselName: this.vessel.name,
                            companyType: this.selectedCompanyType.name,
                            country: this.selectedCountry.country
                        });
                        if (response.isEmailSent) {
                            this.requestSubmitted = true;
                            this.currentStep = "form-submitted";
                        } else {
                            this.notificationClientService.show("Failed to send invite", "", NotificationType.Error, 5000);
                        }
                    } else {
                        const ghgVerification = this.requestAnEnvironmentalVerification.item ?? this.requestAnEnvironmentalVerification.getNewGhgVerification();
                        this.externalGhgVerification = {
                            skipBasicTab: true,
                            ghgVerification: ghgVerification,
                            submit: this.submitClientEnvironmentalVerification,
                            navigateBack: this.goBack,
                            vessel: this.vessel
                        };
                        this.currentStep = "env-verification-form";
                    }
                }
            } catch (e) {
                console.log("Failed to download doc fleet", e);
            }
        }
    }

    public submitClientEnvironmentalVerification = () => {
        return new Promise<SaveGhgVerificationResponse>((resolve, reject) => {

            this.requestAnEnvironmentalVerification.validateDataPoints(true).then(() => {

                const changes = this.requestAnEnvironmentalVerification.getChanges();

                const marketingData: { [key: string]: string } = {};
                marketingData["email"] = this.emailCorrespondences[0];
                marketingData["First Name"] = this.firstName;
                marketingData["Last Name"] = this.lastName;
                marketingData["Job title"] = this.jobTitle;
                marketingData["Company Name"] = this.selectedCompany.fullName;
                marketingData["Company type"] = this.selectedCompanyType.name;
                marketingData["Country"] = this.selectedCountry.country;
                marketingData["T&Cs"] = this.terms ? "true" : "false";
                marketingData["marketing comms"] = this.marketing ? "true" : "false";

                this.ghgService.saveClientGhgVerification({
                    changes: changes,
                    guid: this.externalGhgVerification.ghgVerification.guid,
                    correspondenceEmails: this.emailCorrespondences.map(email => ({
                        email: email,
                        emailRecipientTypeId: EmailRecipientTypeEnum.To
                    })),
                    evdiFiles: this.requestAnEnvironmentalVerification.evdiFiles,
                    capacityPlanFiles: this.requestAnEnvironmentalVerification.capacityPlanFiles,
                    currentClassCertificateFiles: this.requestAnEnvironmentalVerification.currentClassCertificateFiles,
                    mainEngineShopTestFiles: this.requestAnEnvironmentalVerification.mainEngineShopTestFiles,
                    auxiliaryEngineShopTestFiles: this.requestAnEnvironmentalVerification.auxiliaryEngineShopTestFiles,
                    seaTrialReportFiles: this.requestAnEnvironmentalVerification.seaTrialReportFiles,
                    certifiedDocumentsFiles: this.requestAnEnvironmentalVerification.certifiedDocumentsFiles,
                    reliquefactionDocumentationsFiles: this.requestAnEnvironmentalVerification.reliquefactionDocumentationsFiles,
                    energySavingEquipmentFiles: this.requestAnEnvironmentalVerification.energySavingEquipmentFiles,
                    eexiFiles: this.requestAnEnvironmentalVerification.eexiFiles,
                    eeCertificateFiles: this.requestAnEnvironmentalVerification.eeCertificateFiles,
                    ciiFiles: this.requestAnEnvironmentalVerification.ciiFiles,
                    eplShaPoLiFiles: this.requestAnEnvironmentalVerification.eplShaPoLiFiles,
                    marketingData: marketingData
                }).then(x => {
                    this.ghgAlreadyExists = x.ghgAlreadyExists;
                    if (x && x.generatedId > 0) {
                        this.requestAnEnvironmentalVerification.ghgSubmitted = true;
                        this.notificationClientService.show("Environmental review successfully submitted", "", NotificationType.Success);
                        this.requestAnEnvironmentalVerification.clear();
                    } else if (x.ghgAlreadyExists) {
                        this.existingGhg = x.existentGhgId;
                        this.notificationClientService.show("Validation Error", "Environmental Review Request for this vessel already exists in task backlog.", NotificationType.Error);
                    }
                    resolve(x);
                    return;
                },  () => {
                    this.notificationClientService.show("Error submitting Environmental Review", "", NotificationType.Error);
                    reject();
                    return;
                });
            }).catch(() => {
                reject();
                return;
            });
        });
    }
}